import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getApiEndpoint } from '../config/api';
import { getSubscriptionStatus } from '../services/paymentService';
import './CourseGeneratorForm.css';
import TRexGame from './games/TRexGame';
import SnakeGame from './games/SnakeGame';
import RocketGame from './games/RocketGame';

const QUOTA_MODAL_SESSION_KEY = 'rtc-quota-depleted-modal-shown';

function CourseGeneratorForm({ onOpenAuth }) {
  const { isAuthenticated, getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    MATIERE: '',
    NIVEAU: '',
    DUREE: '',
    THEME: '',
    NB_PAGES: '',
    LANGUE: 'Français',
    PLAN_COURS: '',
    COURS_PCT: 70,
    EXEMPLES_PCT: 15,
    EXERCICES_PCT: 15
  });

  const [selectedFormats, setSelectedFormats] = useState({
    tex: false,
    pdf: false,
    docx: false,
    md: false
  });

  const [loading, setLoading] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [error, setError] = useState(null);
  const [generatedFiles, setGeneratedFiles] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [isPlayingGame, setIsPlayingGame] = useState(false); // Pour jouer en dehors du chargement
  const [jobId, setJobId] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [showQuotaModal, setShowQuotaModal] = useState(false);

  // Composant InfoButton pour afficher des informations sur les champs
  const InfoButton = ({ text }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
      <div 
        className="info-button-container"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <button
          type="button"
          className="info-button"
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          aria-label="Information"
          tabIndex={0}
        >
          i
        </button>
        {showTooltip && (
          <div className="info-tooltip">
            <div className="info-tooltip-content">
              {text}
            </div>
            <div className="info-tooltip-arrow"></div>
          </div>
        )}
      </div>
    );
  };

  // Textes descriptifs pour chaque champ
  const fieldDescriptions = {
    MATIERE: "Indiquez la matière enseignée (ex: Physique, Mathématiques, Chimie, etc.). Cette information permet à l'IA de générer un contenu adapté au domaine.",
    NIVEAU: "Décrivez le public et le niveau de votre audience (ex: '1ère année d'école d'ingénieur', 'élèves en difficulté', 'niveau bac+2', etc.). Plus vous êtes précis, meilleur sera le contenu généré.",
    THEME: "Le thème principal de la séance (ex: 'Les 3 lois de Newton', 'Les équations différentielles', etc.). C'est le sujet central autour duquel le cours sera construit.",
    DUREE: "La durée prévue de la séance en minutes (entre 30 et 300 minutes). Cette information aide à calibrer la quantité de contenu à générer.",
    NB_PAGES: "Le nombre de pages souhaité pour le document. Vous pouvez indiquer un nombre précis (ex: '5') ou une plage (ex: 'entre 5 et 8 pages'). Maximum 20 pages.",
    LANGUE: "Choisissez la langue dans laquelle le cours sera rédigé. Tous les contenus (titres, explications, exercices) seront générés dans cette langue.",
    PLAN_COURS: "Optionnel : Décrivez le contenu, les notions ou les points spécifiques que vous souhaitez aborder dans le cours. L'IA structurera le cours autour de ces éléments. Laissez vide pour une génération autonome.",
    COURS_PCT: "Pourcentage du contenu dédié à la partie théorique/cours. La somme des trois pourcentages (Cours + Exemples + Exercices) doit être égale à 100%.",
    EXEMPLES_PCT: "Pourcentage du contenu dédié aux exemples illustratifs. Les exemples aident à comprendre les concepts théoriques.",
    EXERCICES_PCT: "Pourcentage du contenu dédié aux exercices pratiques. Les exercices permettent de mettre en application les connaissances.",
    FORMATS: "Sélectionnez un ou plusieurs formats de sortie pour votre cours : .tex, .pdf, .docx, .md. Vous pouvez sélectionner plusieurs formats pour obtenir le même contenu dans différents formats."
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    if (sessionStorage.getItem(QUOTA_MODAL_SESSION_KEY)) return;

    let cancelled = false;
    getSubscriptionStatus()
      .then((data) => {
        if (cancelled || !data?.quota) return;
        const { hasQuota, remaining } = data.quota;
        if (hasQuota === false && remaining === 0) {
          setShowQuotaModal(true);
          sessionStorage.setItem(QUOTA_MODAL_SESSION_KEY, '1');
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Pré-remplir le formulaire si des données de régénération sont passées
  useEffect(() => {
    if (location.state && location.state.regenerateData) {
      const data = location.state.regenerateData;
      setFormData({
        MATIERE: data.MATIERE || '',
        NIVEAU: data.NIVEAU || '',
        DUREE: data.DUREE || '',
        THEME: data.THEME || '',
        NB_PAGES: data.NB_PAGES || '',
        LANGUE: data.LANGUE || 'Français',
        PLAN_COURS: data.PLAN_COURS || '',
        COURS_PCT: data.COURS_PCT || 70,
        EXEMPLES_PCT: data.EXEMPLES_PCT || 15,
        EXERCICES_PCT: data.EXERCICES_PCT || 15
      });
      
      if (data.formats) {
        setSelectedFormats(data.formats);
      }

      // Nettoyer le state pour éviter de pré-remplir à nouveau si on navigue
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormatChange = (format) => {
    setSelectedFormats(prev => ({
      ...prev,
      [format]: !prev[format]
    }));
  };

  const handlePercentageChange = (e) => {
    const { name, value } = e.target;
    const newValue = Math.max(0, Math.min(100, parseInt(value) || 0));
    
    setFormData(prev => {
      const currentCours = name === 'COURS_PCT' ? newValue : prev.COURS_PCT;
      const currentExemples = name === 'EXEMPLES_PCT' ? newValue : prev.EXEMPLES_PCT;
      const currentExercices = name === 'EXERCICES_PCT' ? newValue : prev.EXERCICES_PCT;
      
      // Logique en cascade :
      // - Si on change Cours → ajuster Exemples (Exercices reste fixe)
      // - Si on change Exemples → ajuster Exercices (Cours reste fixe)
      // - Si on change Exercices → ajuster Exemples (Cours reste fixe)
      
      if (name === 'COURS_PCT') {
        // On ajuste Exemples pour maintenir 100%
        const remaining = 100 - newValue - currentExercices;
        return {
          ...prev,
          COURS_PCT: newValue,
          EXEMPLES_PCT: Math.max(0, remaining),
          EXERCICES_PCT: currentExercices
        };
      } else if (name === 'EXEMPLES_PCT') {
        // On ajuste Exercices pour maintenir 100%
        const remaining = 100 - currentCours - newValue;
        return {
          ...prev,
          COURS_PCT: currentCours,
          EXEMPLES_PCT: newValue,
          EXERCICES_PCT: Math.max(0, remaining)
        };
      } else if (name === 'EXERCICES_PCT') {
        // On ajuste Exemples pour maintenir 100%
        const remaining = 100 - currentCours - newValue;
        return {
          ...prev,
          COURS_PCT: currentCours,
          EXEMPLES_PCT: Math.max(0, remaining),
          EXERCICES_PCT: newValue
        };
      }
      
      return prev;
    });
  };

  // Fonction pour vérifier le statut du job
  const pollJobStatus = async (jobId) => {
    try {
      console.log(`🔄 Polling status pour job: ${jobId}`);
      const token = getToken();
      const config = token ? {
        headers: { Authorization: `Bearer ${token}` }
      } : {};
      
      const response = await axios.get(`${getApiEndpoint('generation')}/status/${jobId}`, config);
      console.log(`📊 Status reçu:`, response.data);
      
      if (response.data.status === 'completed') {
        console.log(`✅ Job completed! Files:`, response.data.files);
        // Job terminé avec succès
        setStatusMessage('Génération terminée !');
        
        // Vérifier si on a les fichiers ET le sessionId
        if (response.data.files && response.data.sessionId && response.data.files.length > 0) {
          // Cas normal : on a les fichiers, on les affiche
          setGeneratedFiles(response.data.files);
          setSessionId(response.data.sessionId);
          
          // Afficher la notification de succès
          setShowSuccessNotification(true);
          
          // Jouer un son de notification
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZSR0PVK/m77FdGglBm9/zx3ElBSyBzvLYiTcIGWi77eefTRAMUKfj8LZjHAY4ktfyy3ksBSR3x/DdkEAKFF606eunVRQKRp/g8r5sIQUxh9Hz04IzBh5uwO/jmUkdD1Sv5u+xXRoJQZvf88dxJQUscc7y2Ik3CBlou+3nn00QDFCU4/C2YxwGOJLX8st5LAUkd8fw3ZBACxRctOnrp1UUCkaf4PK+bCEFMYfR89OCMwYebrzvnpg4CwQ==');
            audio.volume = 0.5;
            audio.play().catch(() => {});
          } catch (e) {}
        } else {
          // Cas où le job est terminé mais on n'a pas les fichiers (requiresAuth ou autre)
          setStatusMessage(response.data.message || 'Génération terminée ! Vérifiez votre email.');
          setShowSuccessNotification(true);
        }
        
        setLoading(false); // Important : toujours fermer le popup de chargement
        return true; // Arrêter le polling
      } else if (response.data.status === 'failed') {
        // Job échoué
        setError(response.data.error || 'La génération a échoué');
        setLoading(false);
        return true; // Arrêter le polling
      } else {
        // Job en cours (pending ou processing)
        setStatusMessage(response.data.message || 'Génération en cours...');
        return false; // Continuer le polling
      }
    } catch (err) {
      console.error('Erreur lors de la vérification du statut:', err);
      // Ne pas arrêter le polling en cas d'erreur réseau
      return false;
    }
  };

  // Fonction pour démarrer le polling
  const startPolling = (jobId) => {
    console.log(`🚀 Démarrage du polling pour job: ${jobId}`);
    const pollInterval = setInterval(async () => {
      const shouldStop = await pollJobStatus(jobId);
      if (shouldStop) {
        console.log(`⏹️  Arrêt du polling`);
        clearInterval(pollInterval);
      }
    }, 2000); // Vérifier toutes les 2 secondes
    
    // Arrêter le polling après 5 minutes maximum
    setTimeout(() => {
      clearInterval(pollInterval);
      if (loading) {
        setError('Le délai de génération a expiré. Vérifiez votre email ou votre historique.');
        setLoading(false);
      }
    }, 300000); // 5 minutes
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier l'authentification
    if (!isAuthenticated) {
      setError('Vous devez être connecté pour générer un cours');
      if (onOpenAuth) onOpenAuth();
      return;
    }
    
    // Validation de la somme des pourcentages
    const total = formData.COURS_PCT + formData.EXEMPLES_PCT + formData.EXERCICES_PCT;
    if (total !== 100) {
      setError(`La somme des pourcentages doit être égale à 100% (actuellement : ${total}%)`);
      return;
    }

    // Vérifier qu'au moins un format est sélectionné
    console.log('DEBUG selectedFormats:', JSON.stringify(selectedFormats));
    const hasSelectedFormat = selectedFormats.pdf || selectedFormats.tex || selectedFormats.docx || selectedFormats.md;
    console.log('DEBUG hasSelectedFormat:', hasSelectedFormat);
    if (!hasSelectedFormat) {
      setError('Veuillez sélectionner au moins un format de sortie');
      return;
    }

    setLoading(true);
    setError(null);
    setGeneratedFiles(null);
    setShowSuccessNotification(false);
    setStatusMessage('Démarrage de la génération...');

    try {
      // Construire la répartition au format attendu par le backend
      const REPARTITION = `Cours:${formData.COURS_PCT}%,Exemples:${formData.EXEMPLES_PCT}%,Exercices:${formData.EXERCICES_PCT}%`;
      
      const requestData = {
        ...formData,
        REPARTITION,
        formats: selectedFormats
      };
      
      // Ajouter le token d'authentification dans les headers
      const token = getToken();
      const config = token ? {
        headers: { Authorization: `Bearer ${token}` }
      } : {};
      
      const response = await axios.post(`${getApiEndpoint('generation')}/generate`, requestData, config);
      
      if (response.data.success && response.data.jobId) {
        // Nouveau système de jobs asynchrones
        setJobId(response.data.jobId);
        setStatusMessage(response.data.message || 'Génération en cours...');
        
        // Démarrer le polling du statut
        startPolling(response.data.jobId);
      } else {
        setError('Erreur lors de la génération des fichiers');
        setLoading(false);
      }
    } catch (err) {
      console.error('Erreur:', err);
      if (err.response?.status === 429) {
        setShowQuotaModal(true);
        setError(null);
      } else {
        setError(err.response?.data?.message || 'Une erreur est survenue lors de la génération');
      }
      setLoading(false);
    }
  };

  const downloadFile = async (downloadUrl) => {
    try {
      // Utiliser fetch pour récupérer le blob avec le token d'authentification
      const token = getToken();
      const response = await fetch(downloadUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }
      
      // Récupérer le blob
      const blob = await response.blob();
      
      // Créer un lien temporaire pour télécharger le blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extraire le nom du fichier depuis le header Content-Disposition
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'fichier';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Libérer l'URL du blob
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement du fichier:', error);
      alert('Erreur lors du téléchargement. Veuillez réessayer.');
    }
  };

  const downloadAllFiles = async () => {
    try {
      const downloadAllUrl = `${getApiEndpoint('generation')}/download-all/${sessionId}`;
      
      // Utiliser fetch pour récupérer le blob avec le token d'authentification
      const token = getToken();
      const response = await fetch(downloadAllUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }
      
      // Récupérer le blob
      const blob = await response.blob();
      
      // Créer un lien temporaire pour télécharger le blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extraire le nom du fichier depuis le header Content-Disposition
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'seance_complete.zip';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Libérer l'URL du blob
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement du ZIP:', error);
      alert('Erreur lors du téléchargement. Veuillez réessayer.');
    }
  };

  const openGame = (gameName) => {
    setSelectedGame(gameName);
    setIsPlayingGame(true);
  };

  const closeGame = () => {
    setSelectedGame(null);
    setIsPlayingGame(false);
  };

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="course-form">
        <div className="form-section">
          <h2>Informations de base</h2>

          <div className="form-group">
            <label htmlFor="LANGUE" className="label-with-info">
              Langue du cours *
              <InfoButton text={fieldDescriptions.LANGUE} />
            </label>
            <select
              id="LANGUE"
              name="LANGUE"
              value={formData.LANGUE}
              onChange={handleChange}
              required
            >
              <option value="Français">Français</option>
              <option value="Anglais">Anglais</option>
              <option value="Allemand">Allemand</option>
              <option value="Espagnol">Espagnol</option>
              <option value="Italien">Italien</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="MATIERE" className="label-with-info">
              Matière *
              <InfoButton text={fieldDescriptions.MATIERE} />
            </label>
            <input
              type="text"
              id="MATIERE"
              name="MATIERE"
              value={formData.MATIERE}
              onChange={handleChange}
              placeholder="Ex: Physique, Mathématiques, Chimie..."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="NIVEAU" className="label-with-info">
              Public et niveau *
              <InfoButton text={fieldDescriptions.NIVEAU} />
            </label>
            <input
              type="text"
              id="NIVEAU"
              name="NIVEAU"
              value={formData.NIVEAU}
              onChange={handleChange}
              placeholder="Ex: 1ère année d'école d'ingénieur, élèves en difficulté"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="THEME" className="label-with-info">
              Thème principal *
              <InfoButton text={fieldDescriptions.THEME} />
            </label>
            <input
              type="text"
              id="THEME"
              name="THEME"
              value={formData.THEME}
              onChange={handleChange}
              placeholder="Ex: Les 3 lois de Newton"
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Paramètres de la séance</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="DUREE" className="label-with-info">
                Durée (minutes) *
                <InfoButton text={fieldDescriptions.DUREE} />
              </label>
              <input
                type="number"
                id="DUREE"
                name="DUREE"
                value={formData.DUREE}
                onChange={handleChange}
                placeholder="90"
                min="30"
                max="300"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="NB_PAGES" className="label-with-info">
                Nombre de pages * <span className="max-indicator">(max. 20)</span>
                <InfoButton text={fieldDescriptions.NB_PAGES} />
              </label>
              <input
                type="text"
                id="NB_PAGES"
                name="NB_PAGES"
                value={formData.NB_PAGES}
                onChange={handleChange}
                placeholder="Ex: 5, 5-8, entre 5 et 8 pages"
                required
              />
              <small className="help-text">Indiquez un nombre ou une plage (ex: "entre 5 et 8 pages") - Maximum 20 pages</small>
            </div>
          </div>

          <div className="mb-8">
            <label className="block font-semibold text-gray-800 mb-2 text-[0.95rem]" style={{ fontWeight: 600, color: '#333333' }}>
              Répartition du contenu * (total = 100%)
              <InfoButton text={fieldDescriptions.COURS_PCT} />
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
              {/* Cours */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <label htmlFor="COURS_PCT" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', width: '7rem' }}>Cours</label>
                <input
                  type="number"
                  id="COURS_PCT"
                  name="COURS_PCT"
                  value={formData.COURS_PCT}
                  onChange={handlePercentageChange}
                  min="0"
                  max="100"
                  style={{ 
                    width: '5rem', 
                    padding: '0.5rem 0.75rem', 
                    backgroundColor: 'white', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '0.5rem',
                    outline: 'none',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                  }}
                  required
                />
                <span style={{ color: '#4b5563', fontWeight: 500 }}>%</span>
                <input
                  type="range"
                  name="COURS_PCT"
                  value={formData.COURS_PCT}
                  onChange={handlePercentageChange}
                  min="0"
                  max="100"
                  style={{ 
                    flex: 1,
                    height: '0.375rem',
                    borderRadius: '9999px',
                    background: `linear-gradient(to right, #1e3a8a 0%, #1e3a8a ${formData.COURS_PCT}%, #e5e7eb ${formData.COURS_PCT}%, #e5e7eb 100%)`,
                    outline: 'none',
                    WebkitAppearance: 'none',
                    appearance: 'none'
                  }}
                />
              </div>
              
              {/* Exemples */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <label htmlFor="EXEMPLES_PCT" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', width: '7rem' }}>Exemples</label>
                <input
                  type="number"
                  id="EXEMPLES_PCT"
                  name="EXEMPLES_PCT"
                  value={formData.EXEMPLES_PCT}
                  onChange={handlePercentageChange}
                  min="0"
                  max="100"
                  style={{ 
                    width: '5rem', 
                    padding: '0.5rem 0.75rem', 
                    backgroundColor: 'white', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '0.5rem',
                    outline: 'none',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                  }}
                  required
                />
                <span style={{ color: '#4b5563', fontWeight: 500 }}>%</span>
                <input
                  type="range"
                  name="EXEMPLES_PCT"
                  value={formData.EXEMPLES_PCT}
                  onChange={handlePercentageChange}
                  min="0"
                  max="100"
                  style={{ 
                    flex: 1,
                    height: '0.375rem',
                    borderRadius: '9999px',
                    background: `linear-gradient(to right, #dc2626 0%, #dc2626 ${formData.EXEMPLES_PCT}%, #e5e7eb ${formData.EXEMPLES_PCT}%, #e5e7eb 100%)`,
                    outline: 'none',
                    WebkitAppearance: 'none',
                    appearance: 'none'
                  }}
                />
              </div>
              
              {/* Exercices */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <label htmlFor="EXERCICES_PCT" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', width: '7rem' }}>Exercices</label>
                <input
                  type="number"
                  id="EXERCICES_PCT"
                  name="EXERCICES_PCT"
                  value={formData.EXERCICES_PCT}
                  onChange={handlePercentageChange}
                  min="0"
                  max="100"
                  style={{ 
                    width: '5rem', 
                    padding: '0.5rem 0.75rem', 
                    backgroundColor: 'white', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '0.5rem',
                    outline: 'none',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                  }}
                  required
                />
                <span style={{ color: '#4b5563', fontWeight: 500 }}>%</span>
                <input
                  type="range"
                  name="EXERCICES_PCT"
                  value={formData.EXERCICES_PCT}
                  onChange={handlePercentageChange}
                  min="0"
                  max="100"
                  style={{ 
                    flex: 1,
                    height: '0.375rem',
                    borderRadius: '9999px',
                    background: `linear-gradient(to right, #1e3a8a 0%, #1e3a8a ${formData.EXERCICES_PCT}%, #e5e7eb ${formData.EXERCICES_PCT}%, #e5e7eb 100%)`,
                    outline: 'none',
                    WebkitAppearance: 'none',
                    appearance: 'none'
                  }}
                />
              </div>
              
              {/* Total */}
              <div style={{ paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                <p style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: 500,
                  color: (formData.COURS_PCT + formData.EXEMPLES_PCT + formData.EXERCICES_PCT) === 100 ? '#1d4ed8' : '#dc2626'
                }}>
                  Total : {formData.COURS_PCT + formData.EXEMPLES_PCT + formData.EXERCICES_PCT}%
                  {(formData.COURS_PCT + formData.EXEMPLES_PCT + formData.EXERCICES_PCT) !== 100 && (
                    <span> - Doit être égal à 100%</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="PLAN_COURS" className="label-with-info">
              Contenu abordé (optionnel)
              <InfoButton text={fieldDescriptions.PLAN_COURS} />
            </label>
            <textarea
              id="PLAN_COURS"
              name="PLAN_COURS"
              value={formData.PLAN_COURS}
              onChange={handleChange}
              placeholder="Décrivez ici le contenu, les notions ou points spécifiques à aborder dans le cours..."
              rows="4"
            />
            <small className="help-text">
              Laissez vide pour une génération autonome du contenu
            </small>
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title-with-info">
            Formats de sortie
            <InfoButton text={fieldDescriptions.FORMATS} />
          </h2>
          <p className="section-description">Sélectionnez les formats que vous souhaitez générer (au moins un) :</p>
          
          <div className="formats-list">
            <label className="format-item">
              <div className="format-info">
                <div className="format-text">
                  <span className="format-name">.tex</span>
                  <span className="format-description">LaTeX source</span>
                </div>
              </div>
              <input
                type="checkbox"
                checked={selectedFormats.tex}
                onChange={() => handleFormatChange('tex')}
              />
            </label>

            <label className="format-item">
              <div className="format-info">
                <div className="format-text">
                  <span className="format-name">.pdf</span>
                  <span className="format-description">Document PDF</span>
                </div>
              </div>
              <input
                type="checkbox"
                checked={selectedFormats.pdf}
                onChange={() => handleFormatChange('pdf')}
              />
            </label>

            <label className="format-item">
              <div className="format-info">
                <div className="format-text">
                  <span className="format-name">.docx</span>
                  <span className="format-description">Word / Google Docs</span>
                </div>
              </div>
              <input
                type="checkbox"
                checked={selectedFormats.docx}
                onChange={() => handleFormatChange('docx')}
              />
            </label>

            <label className="format-item">
              <div className="format-info">
                <div className="format-text">
                  <span className="format-name">.md</span>
                  <span className="format-description">Markdown</span>
                </div>
              </div>
              <input
                type="checkbox"
                checked={selectedFormats.md}
                onChange={() => handleFormatChange('md')}
              />
            </label>
          </div>
        </div>

        <button 
          type="submit" 
          className="submit-button"
          disabled={loading}
        >
          {loading ? 'Génération en cours...' : 'Générer la séance'}
        </button>
      </form>

      {/* Section Mini-jeux accessible en permanence */}
      <div className="mini-games-section">
        <h3>🎮 Mini-jeux</h3>
        <p className="section-description">Jouez pendant la génération de votre cours ou simplement pour vous détendre !</p>
        <div className="games-grid-permanent">
          <button 
            className="game-card-permanent"
            onClick={() => openGame('trex')}
            disabled={loading}
          >
            <div className="game-icon game-icon-trex"></div>
            <h5>🦖 T-Rex Runner</h5>
            <p>Sautez par-dessus les obstacles</p>
          </button>

          <button 
            className="game-card-permanent"
            onClick={() => openGame('snake')}
            disabled={loading}
          >
            <div className="game-icon game-icon-snake"></div>
            <h5>🐍 Snake Academy</h5>
            <p>Collectez des livres</p>
          </button>

          <button 
            className="game-card-permanent"
            onClick={() => openGame('rocket')}
            disabled={loading}
          >
            <div className="game-icon game-icon-rocket"></div>
            <h5>🚀 Rocket Game</h5>
            <p>Évitez les astéroïdes</p>
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-container">
            <div className="spinner"></div>
            <h3>Vous receverez un email une fois le traitement terminé</h3>
            <p className="loading-info">
              {statusMessage || 'Le processus de génération avec l\'IA peut prendre entre 30 secondes et 2 minutes.'}
              <br />
              En attendant, jouez à un mini-jeu !
            </p>

            {!selectedGame && (
              <div className="games-selector">
                <h4>Choisissez votre jeu :</h4>
                <div className="games-grid">
                  <button 
                    className="game-card"
                    onClick={() => setSelectedGame('trex')}
                  >
                    <div className="game-icon game-icon-trex"></div>
                    <h5>T-Rex Runner</h5>
                    <p>Sautez par-dessus les obstacles</p>
                  </button>

                  <button 
                    className="game-card"
                    onClick={() => setSelectedGame('snake')}
                  >
                    <div className="game-icon game-icon-snake"></div>
                    <h5>Snake Academy</h5>
                    <p>Collectez des livres</p>
                  </button>

                  <button 
                    className="game-card"
                    onClick={() => setSelectedGame('rocket')}
                  >
                    <div className="game-icon game-icon-rocket"></div>
                    <h5>Rocket Game</h5>
                    <p>Évitez les astéroïdes</p>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedGame && (loading || isPlayingGame) && (
        <div className="game-modal-overlay" onClick={closeGame}>
          <div className="game-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-game" onClick={closeGame}>✕</button>
            {selectedGame === 'trex' && <TRexGame />}
            {selectedGame === 'snake' && <SnakeGame />}
            {selectedGame === 'rocket' && <RocketGame />}
          </div>
        </div>
      )}

      {showQuotaModal && (
        <div
          className="quota-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="quota-modal-title"
        >
          <div className="quota-modal">
            <div className="quota-modal-accent" aria-hidden />
            <h2 id="quota-modal-title">Plus de crédits disponibles</h2>
            <p>
              Vous ne pouvez pas lancer de nouvelle génération tant que votre solde est à zéro.
              Consultez nos offres pour recharger ou souscrire à un abonnement.
            </p>
            <div className="quota-modal-actions">
              <button
                type="button"
                className="quota-modal-btn quota-modal-btn-primary"
                onClick={() => {
                  setShowQuotaModal(false);
                  navigate('/pricing');
                }}
              >
                Voir les offres
              </button>
              <button
                type="button"
                className="quota-modal-btn quota-modal-btn-secondary"
                onClick={() => setShowQuotaModal(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification de succès au-dessus du jeu */}
      {showSuccessNotification && (
        <div className="success-notification-overlay">
          <div className="success-notification">
            <div className="success-icon-check"></div>
            <h2>Génération terminée !</h2>
            <p>Les fichiers vont vous être envoyés par email</p>
            <p>Ils sont aussi disponibles dans l'onglet Historique</p>
            <div className="notification-actions">
              <button 
                className="notification-button primary"
                onClick={() => {
                  setShowSuccessNotification(false);
                  closeGame(); // Fermer le jeu
                  navigate('/history'); // Rediriger vers l'historique
                }}
              >
                Voir les fichiers
              </button>
              <button 
                className="notification-button secondary"
                onClick={() => setShowSuccessNotification(false)}
              >
                Continuer à jouer
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <strong>Erreur :</strong> {error}
        </div>
      )}

      {generatedFiles && (
        <div className="results-container">
          <h2>Fichiers générés avec succès !</h2>
          
          <div className="files-list">
            <h3>Fichiers disponibles :</h3>
            {generatedFiles.map((file, index) => (
              <div key={index} className="file-item">
                <span className="file-name">{file.filename}</span>
                <button 
                  onClick={() => downloadFile(file.downloadUrl)}
                  className="download-button"
                >
                  Télécharger
                </button>
              </div>
            ))}
          </div>

          <button 
            onClick={downloadAllFiles}
            className="download-all-button"
          >
            Télécharger tous les fichiers (ZIP)
          </button>
        </div>
      )}
    </div>
  );
}

export default CourseGeneratorForm;
